import Reveal from "../../components/Reveal";
import MarqueeLink from "../../components/MarqueeLink";
import SEO from "../../components/SEO";
import ProjectGallery from "../../components/ProjectGallery";
import NextProject from "../../components/NextProject";
import { PROJECTS } from "../../lib/projects";

const project = PROJECTS.find((p) => p.id === "151-john-street");

const details = [
  { label: "Project", value: project.name },
  { label: "Location", value: "Auckland, New Zealand" },
  { label: "Sector", value: project.category },
  { label: "Scope", value: project.services.join(", ") },
  { label: "Delivered By", value: "Trust Flooring & Interiors" },
];

const overview = [
  "Located in Auckland, 151 John Street showcases the impact that thoughtfully selected flooring can have on a residential interior.",
  "Trust Flooring & Interiors was engaged to deliver the flooring installation, with a focus on quality workmanship, precise detailing and a consistent finish throughout the home.",
  "Our team worked carefully through the preparation and installation process to ensure the flooring complemented the property's architectural design and overall interior aesthetic.",
  "The result is a refined, natural finish that brings warmth, character and a sense of continuity to the living spaces.",
];

export default function JohnStreet() {
  return (
    <div data-testid="john-street-page" className="bg-[#F5F5F0]">
      <SEO
        title="151 John Street – Residential Flooring Project | Trust Flooring & Interiors"
        description="151 John Street, Auckland — a residential flooring installation by Trust Flooring & Interiors, delivering a refined, natural finish with precise detailing throughout the home."
        canonical={`${window.location.origin}/projects/151-john-street`}
      />

      <section className="relative overflow-hidden -mt-32 md:-mt-36 h-[90vh] min-h-[600px]">
        <img
          src={project.image}
          alt="151 John Street — open-plan kitchen and living space with pale oak flooring"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/20 to-black/70" />
        <div className="relative max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16 h-full flex flex-col justify-end pb-24 md:pb-32">
          <Reveal>
            <span className="eyebrow text-white/80">Residential Flooring Project</span>
          </Reveal>
          <Reveal delay={120}>
            <h1 className="mt-8 font-heading text-3xl md:text-5xl lg:text-6xl font-medium text-white tracking-tight leading-[1.1]" data-testid="john-street-title">
              151 John Street
              <span className="sr-only"> – Residential Flooring Project</span>
            </h1>
          </Reveal>
        </div>
      </section>

      <section className="py-32 md:py-48" data-testid="john-street-overview">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16 grid grid-cols-1 md:grid-cols-12 gap-16">
          <div className="md:col-span-5">
            <Reveal>
              <span className="eyebrow">Overview</span>
              <h2 className="mt-8 font-heading text-2xl md:text-3xl text-[#111111] leading-[1.25] font-medium tracking-tight">
                Precision in Every Detail
              </h2>
            </Reveal>
          </div>
          <div className="md:col-span-6 md:col-start-7">
            <Reveal delay={120}>
              <div className="space-y-6 max-w-lg" data-testid="john-street-overview-copy">
                {overview.map((t) => (
                  <p key={t} className="text-[#545454] text-base md:text-lg leading-relaxed">{t}</p>
                ))}
              </div>
            </Reveal>
            <Reveal delay={200}>
              <dl className="mt-14">
                {details.map((d) => (
                  <div key={d.label} className="flex gap-8 py-5 border-t border-[#E2E2DF]">
                    <dt className="w-28 shrink-0 text-[10px] uppercase tracking-[0.25em] text-[#545454] font-heading font-semibold pt-1">
                      {d.label}
                    </dt>
                    <dd className="text-[#111111] font-heading text-sm md:text-base">{d.value}</dd>
                  </div>
                ))}
              </dl>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="pb-32 md:pb-48" data-testid="john-street-gallery-section">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16">
          <Reveal>
            <span className="eyebrow-muted">Gallery</span>
          </Reveal>
          <div className="mt-12">
            <ProjectGallery images={[project.image, ...project.gallery, ...project.featureSlides]} name={project.name} />
          </div>
        </div>
      </section>

      <section className="py-32 md:py-48 border-t border-[#E2E2DF]">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16 grid grid-cols-1 md:grid-cols-12 gap-10 items-end">
          <div className="md:col-span-8">
            <Reveal>
              <h2 className="font-heading text-2xl md:text-3xl lg:text-4xl text-[#111111] tracking-tight font-medium leading-[1.2] max-w-2xl">
                Planning a residence like this? Let's talk.
              </h2>
            </Reveal>
          </div>
          <div className="md:col-span-4 md:text-right">
            <Reveal delay={120}>
              <MarqueeLink to="/contact" variant="primary" data-testid="john-street-cta-contact">
                Get in touch
              </MarqueeLink>
            </Reveal>
          </div>
        </div>
      </section>

      <NextProject currentId={project.id} />
    </div>
  );
}
